import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { Activity, RefreshCw, ChevronLeft, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MetricRow {
  day: string;
  skill_id: string;
  skill_name: string;
  runs: number;
  successes: number;
  success_rate: number;
  p50_ms: number | null;
  p95_ms: number | null;
  avg_quality: number | null;
  sent_to_group: number;
}

interface SendAuditRow {
  skill_name: string;
  success_count: number;
  blocked_count: number;
  last_block_reason: string | null;
}

export default function SkillsObservability() {
  const { toast } = useToast();
  const [metrics, setMetrics] = useState<MetricRow[]>([]);
  const [audit, setAudit] = useState<SendAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: m }, { data: a }] = await Promise.all([
      supabase.from("skill_metrics_daily").select("*").order("day", { ascending: false }).limit(200),
      supabase.from("skill_send_audit")
        .select("skill_name, success, error")
        .gte("created_at", new Date(Date.now() - 7 * 86400_000).toISOString())
        .limit(2000),
    ]);
    setMetrics((m as MetricRow[]) ?? []);
    // Aggregate audit client-side
    const agg = new Map<string, SendAuditRow>();
    (a ?? []).forEach((row: any) => {
      const cur = agg.get(row.skill_name) ?? {
        skill_name: row.skill_name, success_count: 0, blocked_count: 0, last_block_reason: null,
      };
      if (row.success) cur.success_count++;
      else { cur.blocked_count++; cur.last_block_reason = row.error ?? cur.last_block_reason; }
      agg.set(row.skill_name, cur);
    });
    setAudit(Array.from(agg.values()).sort((x, y) => (y.success_count + y.blocked_count) - (x.success_count + x.blocked_count)));
    setLoading(false);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke("skill-metrics-aggregator", { body: {} });
      if (error) throw error;
      toast({ title: "Métricas atualizadas" });
      await load();
    } catch (e) {
      toast({ title: "Falha ao atualizar", description: String(e), variant: "destructive" });
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  // Group metrics by skill
  const bySkill = new Map<string, MetricRow[]>();
  metrics.forEach((r) => {
    const arr = bySkill.get(r.skill_name) ?? [];
    arr.push(r); bySkill.set(r.skill_name, arr);
  });

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Skills", to: "/skills" }, { label: "Observability" }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-headline flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" /> Skills Observability
          </h1>
          <p className="text-muted-foreground text-sm">Métricas diárias por skill e auditoria de envios no WhatsApp (últimos 7 dias).</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/skills"><Button variant="outline"><ChevronLeft className="w-4 h-4 mr-1" /> Voltar</Button></Link>
          <Button onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Atualizar agregação
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Auditoria de envios WhatsApp (7d)</CardTitle>
              <CardDescription>Quantos envios passaram nos guards e quantos foram bloqueados (com último motivo).</CardDescription>
            </CardHeader>
            <CardContent>
              {audit.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem registros de envio recentes.</p>
              ) : (
                <div className="space-y-2">
                  {audit.map((a) => (
                    <div key={a.skill_name} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="font-medium text-sm">{a.skill_name}</div>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="secondary" className="text-emerald-600">
                          ✓ {a.success_count}
                        </Badge>
                        <Badge variant="destructive" className="opacity-80">
                          ⛔ {a.blocked_count}
                        </Badge>
                        {a.last_block_reason && (
                          <span className="text-muted-foreground italic max-w-xs truncate" title={a.last_block_reason}>
                            <AlertCircle className="w-3 h-3 inline mr-1" />{a.last_block_reason}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from(bySkill.entries()).map(([name, rows]) => {
              const today = rows[0];
              const yesterday = rows[1];
              const trend = yesterday ? today.success_rate - yesterday.success_rate : 0;
              return (
                <Card key={name}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{name}</CardTitle>
                      <Badge variant="outline" className="text-xs">{rows.length}d hist</Badge>
                    </div>
                    <CardDescription className="text-xs">{today.day}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <Stat label="Runs" value={String(today.runs)} />
                      <Stat label="Sucesso" value={`${(today.success_rate * 100).toFixed(0)}%`}
                        trend={trend} />
                      <Stat label="P95" value={`${today.p95_ms ?? 0}ms`} />
                    </div>
                    {today.avg_quality !== null && (
                      <div className="text-xs text-muted-foreground">
                        Qualidade média: <span className="font-medium text-foreground">{(today.avg_quality * 100).toFixed(0)}/100</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Enviados ao grupo: <span className="font-medium text-foreground">{today.sent_to_group}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {bySkill.size === 0 && (
            <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
              Sem métricas agregadas ainda. Clique em "Atualizar agregação" para popular.
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, trend }: { label: string; value: string; trend?: number }) {
  return (
    <div className="p-2 bg-muted/30 rounded">
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-sm font-semibold flex items-center justify-center gap-1">
        {value}
        {trend !== undefined && trend !== 0 && (
          trend > 0 ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-destructive" />
        )}
      </div>
    </div>
  );
}
